// store/staffSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/employees/`;

// Fetch minimal staff data for dropdowns and lists
export const fetchStaffMinimal = createAsyncThunk('staff/fetchStaffMinimal', async (params = {}, thunkAPI) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add filter parameters if provided
    if (params.employee_type) queryParams.append('employee_type', params.employee_type);
    if (params.is_archived !== undefined) queryParams.append('is_archived', params.is_archived);
    
    // Fix URL construction - BASE_URL already ends with '/', so we need 'minimal/' not '/minimal/'
    const url = `${BASE_URL}minimal/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching staff minimal:', error);
    return thunkAPI.rejectWithValue(error.response?.data || error.message || 'Fetch failed');
  }
});

export const fetchStaff = createAsyncThunk('staff/fetchStaff', async (_, thunkAPI) => {
  try {
    const response = await axios.get(BASE_URL);
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || 'Fetch failed');
  }
});

export const createVirtualTransaction = createAsyncThunk(
  'staff/createVirtualTransaction',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}employee-virtual-transactions/`,
        data
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل إنشاء المعاملة');
    }
  }
);

export const deleteVirtualTransaction = createAsyncThunk(
  'staff/deleteVirtualTransaction',
  async (transactionId, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}virtual-transactions/delete/${transactionId}/`);
      return transactionId;
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'فشل في حذف المعاملة';
      
      if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      return thunkAPI.rejectWithValue({
        message: errorMessage,
        originalError: errorData
      });
    }
  }
);

export const createStaff = createAsyncThunk('staff/createStaff', async (newStaff, thunkAPI) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/employees/`,
      newStaff,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }
    );
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || 'Create failed');
  }
});

export const archiveStaff = createAsyncThunk(
  'staff/archiveStaff',
  async ({ id, is_archived }, thunkAPI) => {
    try {
      const response = await axios.patch(`${BASE_URL}${id}/`, { is_archived });
      return response.data;
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'فشل في الأرشفة';
      
      if (errorData) {
        if (errorData.is_archived && Array.isArray(errorData.is_archived)) {
          errorMessage = errorData.is_archived[0];
        } else if (errorData.is_archived && typeof errorData.is_archived === 'string') {
          errorMessage = errorData.is_archived;
        } else if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          errorMessage = errorData.non_field_errors[0];
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      return thunkAPI.rejectWithValue({
        message: errorMessage,
        originalError: errorData
      });
    }
  }
);

export const fetchSingleStaff = createAsyncThunk(
  'staff/fetchSingleStaff',
  async (id, thunkAPI) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/employees/${id}/`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'الموظف غير موجود');
    }
  }
);

export const deleteStaff = createAsyncThunk('staff/deleteStaff', async (id, thunkAPI) => {
  try {
    await axios.delete(`${BASE_URL}${id}/`);
    return id;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || 'Delete failed');
  }
});

// Teacher Absence Actions
const ABSENCE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/employees/teacher-absences/`;

export const fetchTeacherAbsences = createAsyncThunk(
  'staff/fetchTeacherAbsences',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.teacher) queryParams.append('teacher', params.teacher);
      if (params.absence_date) queryParams.append('absence_date', params.absence_date);
      
      const url = queryParams.toString() ? `${ABSENCE_URL}?${queryParams}` : ABSENCE_URL;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل جلب سجلات الغياب');
    }
  }
);

export const createTeacherAbsence = createAsyncThunk(
  'staff/createTeacherAbsence',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post(ABSENCE_URL, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل تسجيل الغياب');
    }
  }
);

export const bulkCreateTeacherAbsences = createAsyncThunk(
  'staff/bulkCreateTeacherAbsences',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post(`${ABSENCE_URL}bulk-create/`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل تسجيل الغياب الجماعي');
    }
  }
);

export const deleteTeacherAbsence = createAsyncThunk(
  'staff/deleteTeacherAbsence',
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${ABSENCE_URL}${id}/`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل حذف سجل الغياب');
    }
  }
);

export const updateTeacherAbsence = createAsyncThunk(
  'staff/updateTeacherAbsence',
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await axios.put(`${ABSENCE_URL}${id}/`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل تحديث سجل الغياب');
    }
  }
);

// Employee Absence Actions (any employee type, with optional fee/discount deduction)
const EMPLOYEE_ABSENCE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/employees/employee-absences/`;

export const fetchEmployeeAbsences = createAsyncThunk(
  'staff/fetchEmployeeAbsences',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.employee) queryParams.append('employee', params.employee);
      if (params.absence_date) queryParams.append('absence_date', params.absence_date);

      const url = queryParams.toString() ? `${EMPLOYEE_ABSENCE_URL}?${queryParams}` : EMPLOYEE_ABSENCE_URL;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل جلب سجلات الغياب');
    }
  }
);

export const createEmployeeAbsence = createAsyncThunk(
  'staff/createEmployeeAbsence',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post(EMPLOYEE_ABSENCE_URL, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل تسجيل الغياب');
    }
  }
);

export const deleteEmployeeAbsence = createAsyncThunk(
  'staff/deleteEmployeeAbsence',
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${EMPLOYEE_ABSENCE_URL}${id}/`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل حذف سجل الغياب');
    }
  }
);

export const updateStaff = createAsyncThunk(
  'staff/updateStaff',
  async ({ id, staff }, thunkAPI) => {
    try {
      const formData = new FormData();
      
      // Fields that should be excluded from FormData
      const excludedFields = [
        'id', 
        'history', 
        'payments', 
        'virtual_transactions', 
        'documents',
        'created_at',
        'contract_pdf_url' // Exclude the URL field
      ];

      // Fields that need special handling for complex objects
      const complexFields = ['payments', 'virtual_transactions', 'documents'];

      for (const key in staff) {
        const value = staff[key];
        
        // Skip null, undefined, and excluded fields
        if (value === null || value === undefined || excludedFields.includes(key)) {
          continue;
        }

        // Handle file uploads specifically
        if (key === 'contract_pdf') {
          // Only append if it's actually a File object (new upload)
          if (value instanceof File) {
            formData.append(key, value);
          }
          // Skip if it's a string URL (existing file) - don't send it
          continue;
        }

        // Handle complex objects that shouldn't be sent as form data
        if (complexFields.includes(key)) {
          continue;
        }

        // Handle arrays and objects
        if (Array.isArray(value)) {
          // For arrays, you might want to serialize them or handle differently
          // Skip arrays for now unless specifically needed
          continue;
        } else if (typeof value === 'object' && value !== null) {
          // For objects, you might want to serialize them
          // Skip objects for now unless specifically needed
          continue;
        } else {
          // For primitive values, convert to string and append
          formData.append(key, String(value));
        }
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/employees/${id}/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'فشل في التحديث');
    }
  }
);

const staffSlice = createSlice({
  name: 'staff',
  initialState: {
    list: [],
    filtered: [],
    minimal: [], // New state for minimal data
    filters: {
      searchQuery: '',
      department: '',
      status: '',
    },
    employeeTransactions: {
      payments: [],
      virtuals: [],
    },
    current: null,
    status: 'idle',
    minimalStatus: 'idle', // Status for minimal data
    error: null,
    archiveError: null,
    deleteTransactionError: null,
    // Teacher absence state
    teacherAbsences: [],
    absenceLoading: false,
    absenceError: null,
    // Employee absence state (any employee type, with optional fee/discount)
    employeeAbsences: [],
    employeeAbsenceLoading: false,
    employeeAbsenceError: null,
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
      staffSlice.caseReducers.applyFilters(state);
    },
    setDepartmentFilter: (state, action) => {
      state.filters.department = action.payload;
      staffSlice.caseReducers.applyFilters(state);
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      staffSlice.caseReducers.applyFilters(state);
    },
    clearArchiveError: (state) => {
      state.archiveError = null;
    },
    clearDeleteTransactionError: (state) => {
      state.deleteTransactionError = null;
    },
    applyFilters: (state) => {
      let filtered = [...state.list];
      const { searchQuery, department, status } = state.filters;

      if (searchQuery) {
        filtered = filtered.filter((s) =>
          s.first_name.includes(searchQuery) ||
          s.last_name.includes(searchQuery) ||
          (s.email || '').includes(searchQuery) ||
          (s.phone_number || '').includes(searchQuery)
        );
      }
      if (department) {
        filtered = filtered.filter((s) => s.employee_type?.display_value === department);
      }
      if (status) {
        const isActive = status === 'نشط';
        filtered = filtered.filter((s) => s.is_archived !== isActive);
      }

      state.filtered = filtered;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch minimal staff
      .addCase(fetchStaffMinimal.pending, (state) => {
        state.minimalStatus = 'loading';
      })
      .addCase(fetchStaffMinimal.fulfilled, (state, action) => {
        state.minimalStatus = 'succeeded';
        state.minimal = action.payload;
      })
      .addCase(fetchStaffMinimal.rejected, (state, action) => {
        state.minimalStatus = 'failed';
        state.error = action.payload;
      })
      // Fetch full staff
      .addCase(fetchStaff.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload;
        state.filtered = action.payload;
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(createVirtualTransaction.fulfilled, (state, action) => {
        if (state.current && state.current.id === action.payload.employee) {
          if (!state.current.virtual_transactions) {
            state.current.virtual_transactions = [];
          }
          state.current.virtual_transactions.push(action.payload);
        }
      })
      // Delete virtual transaction
      .addCase(deleteVirtualTransaction.pending, (state) => {
        state.deleteTransactionError = null;
      })
      .addCase(deleteVirtualTransaction.fulfilled, (state, action) => {
        const deletedTransactionId = action.payload;
        
        // Remove from current employee's virtual transactions if it exists
        if (state.current && state.current.virtual_transactions) {
          state.current.virtual_transactions = state.current.virtual_transactions.filter(
            transaction => transaction.id !== deletedTransactionId
          );
        }
        
        // Also remove from list and filtered arrays if they contain virtual transactions
        state.list = state.list.map(employee => ({
          ...employee,
          virtual_transactions: employee.virtual_transactions?.filter(
            transaction => transaction.id !== deletedTransactionId
          ) || []
        }));
        
        state.filtered = state.filtered.map(employee => ({
          ...employee,
          virtual_transactions: employee.virtual_transactions?.filter(
            transaction => transaction.id !== deletedTransactionId
          ) || []
        }));
        
        state.deleteTransactionError = null;
      })
      .addCase(deleteVirtualTransaction.rejected, (state, action) => {
        state.deleteTransactionError = action.payload;
      })
      // Archive staff
      .addCase(archiveStaff.pending, (state) => {
        state.archiveError = null;
      })
      .addCase(archiveStaff.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.list.findIndex(s => s.id === updated.id);
        if (idx !== -1) {
          state.list[idx] = updated;
        }
        const fIdx = state.filtered.findIndex(s => s.id === updated.id);
        if (fIdx !== -1) {
          state.filtered[fIdx] = updated;
        }
        
        // Update minimal list as well
        const minIdx = state.minimal.findIndex(s => s.id === updated.id);
        if (minIdx !== -1) {
          state.minimal[minIdx] = {
            ...state.minimal[minIdx],
            is_archived: updated.is_archived
          };
        }
        
        state.archiveError = null;
      })
      .addCase(archiveStaff.rejected, (state, action) => {
        state.archiveError = action.payload;
      })
      // Fetch single staff
      .addCase(fetchSingleStaff.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSingleStaff.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.current = action.payload;
      })
      .addCase(fetchSingleStaff.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.current = null;
      })
      // Create staff
      .addCase(createStaff.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.filtered.push(action.payload);
        
        // Add to minimal list as well with required fields
        const minimalData = {
          id: action.payload.id,
          first_name: action.payload.first_name,
          last_name: action.payload.last_name,
          full_name: `${action.payload.first_name} ${action.payload.last_name}`,
          employee_id: action.payload.employee_id,
          employee_type_name: action.payload.employee_type?.name,
          is_teacher: action.payload.is_teacher,
          is_driver: action.payload.is_driver,
          is_archived: action.payload.is_archived
        };
        state.minimal.push(minimalData);
      })
      // Delete staff
      .addCase(deleteStaff.fulfilled, (state, action) => {
        state.list = state.list.filter(s => s.id !== action.payload);
        state.filtered = state.filtered.filter(s => s.id !== action.payload);
        state.minimal = state.minimal.filter(s => s.id !== action.payload);
      })
      // Update staff
      .addCase(updateStaff.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateStaff.fulfilled, (state, action) => {
        state.status = 'succeeded';
      
        // Update in list and filtered
        const updated = action.payload;
        const index = state.list.findIndex(s => s.id === updated.id);
        if (index !== -1) {
          state.list[index] = updated;
        }
        const fIndex = state.filtered.findIndex(s => s.id === updated.id);
        if (fIndex !== -1) {
          state.filtered[fIndex] = updated;
        }
        
        // Update minimal list
        const minIndex = state.minimal.findIndex(s => s.id === updated.id);
        if (minIndex !== -1) {
          state.minimal[minIndex] = {
            id: updated.id,
            first_name: updated.first_name,
            last_name: updated.last_name,
            full_name: `${updated.first_name} ${updated.last_name}`,
            employee_id: updated.employee_id,
            employee_type_name: updated.employee_type?.name,
            is_teacher: updated.is_teacher,
            is_driver: updated.is_driver,
            is_archived: updated.is_archived
          };
        }
      
        // Also update current
        state.current = updated;
      })
      .addCase(updateStaff.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Teacher Absence reducers
      .addCase(fetchTeacherAbsences.pending, (state) => {
        state.absenceLoading = true;
        state.absenceError = null;
      })
      .addCase(fetchTeacherAbsences.fulfilled, (state, action) => {
        state.absenceLoading = false;
        state.teacherAbsences = action.payload;
      })
      .addCase(fetchTeacherAbsences.rejected, (state, action) => {
        state.absenceLoading = false;
        state.absenceError = action.payload;
      })
      .addCase(createTeacherAbsence.fulfilled, (state, action) => {
        state.teacherAbsences.push(action.payload);
      })
      .addCase(bulkCreateTeacherAbsences.fulfilled, (state, action) => {
        // Add newly created absences to the list
        if (action.payload.created_absences) {
          state.teacherAbsences = [...state.teacherAbsences, ...action.payload.created_absences];
        }
      })
      .addCase(deleteTeacherAbsence.fulfilled, (state, action) => {
        state.teacherAbsences = state.teacherAbsences.filter(
          absence => absence.id !== action.payload
        );
      })
      .addCase(updateTeacherAbsence.fulfilled, (state, action) => {
        const index = state.teacherAbsences.findIndex(
          absence => absence.id === action.payload.id
        );
        if (index !== -1) {
          state.teacherAbsences[index] = action.payload;
        }
      })
      // Employee Absence reducers
      .addCase(fetchEmployeeAbsences.pending, (state) => {
        state.employeeAbsenceLoading = true;
        state.employeeAbsenceError = null;
      })
      .addCase(fetchEmployeeAbsences.fulfilled, (state, action) => {
        state.employeeAbsenceLoading = false;
        state.employeeAbsences = action.payload;
      })
      .addCase(fetchEmployeeAbsences.rejected, (state, action) => {
        state.employeeAbsenceLoading = false;
        state.employeeAbsenceError = action.payload;
      })
      .addCase(createEmployeeAbsence.fulfilled, (state, action) => {
        state.employeeAbsences.push(action.payload);
      })
      .addCase(deleteEmployeeAbsence.fulfilled, (state, action) => {
        state.employeeAbsences = state.employeeAbsences.filter(
          absence => absence.id !== action.payload
        );
      });
  }
});

export const {
  setSearchQuery,
  setDepartmentFilter,
  setStatusFilter,
  applyFilters,
  clearArchiveError,
  clearDeleteTransactionError
} = staffSlice.actions;

export default staffSlice.reducer;